import type { LoaderFunctionArgs } from '@remix-run/node';
import { json } from '@remix-run/server-runtime';
import { z } from 'zod';
import type {
    MgeGuestEmailRegisteredOnEventQuery,
    MgeGuestEmailRegisteredOnEventQueryVariables,
} from 'generated/graphql';
import { MgeGuestEmailRegisteredOnEventDocument } from 'generated/graphql';
import { HDE_GUEST_EMAIL_ALREADY_REGISTERED_MESSAGE } from '~/modules/hde/constants';

export const HDE_GUEST_EMAIL_CHECK_API_ROUTE =
    '/remix-api/app/hde-guest-email-check';

const QUERY_SCHEMA = z.object({
    eventId: z.coerce.number().int().positive(),
    guestEmail: z.string().trim().email(),
    excludeSubmissionId: z.coerce.number().int().positive().optional(),
});

interface GuestEmailCheckResponse {
    eventId?: number;
    guestEmail?: string;
    /** True when the email already holds a live ticket on the event. */
    isRegistered: boolean;
    error?: string;
}

/**
 * Checks whether a guest invite email is already registered for an HDE event.
 * Drives inline validation on the guest-invitation step before submit.
 */
export const loader = async ({ context, request }: LoaderFunctionArgs) => {
    const url = new URL(request.url);
    const result = QUERY_SCHEMA.safeParse({
        eventId: url.searchParams.get('eventId'),
        guestEmail: url.searchParams.get('guestEmail'),
        excludeSubmissionId:
            url.searchParams.get('excludeSubmissionId') ?? undefined,
    });

    if (!result.success) {
        return json<GuestEmailCheckResponse>(
            {
                isRegistered: false,
                error: 'Invalid query parameters',
            },
            { status: 400 },
        );
    }

    const { eventId, guestEmail, excludeSubmissionId } = result.data;

    try {
        const queryResult = await context.client.query<
            MgeGuestEmailRegisteredOnEventQuery,
            MgeGuestEmailRegisteredOnEventQueryVariables
        >({
            query: MgeGuestEmailRegisteredOnEventDocument,
            variables: {
                EventID: eventId,
                Email: guestEmail,
                ExcludeSubmissionID: excludeSubmissionId,
            },
        });

        const isRegistered =
            queryResult.data?.mgeGuestEmailRegisteredOnEvent ?? false;

        return json<GuestEmailCheckResponse>({
            eventId,
            guestEmail,
            isRegistered,
            ...(isRegistered
                ? { error: HDE_GUEST_EMAIL_ALREADY_REGISTERED_MESSAGE }
                : {}),
        });
    } catch (error) {
        context.logger.error(`Error in ${HDE_GUEST_EMAIL_CHECK_API_ROUTE}`, {
            error,
        });
        return json<GuestEmailCheckResponse>(
            {
                eventId,
                guestEmail,
                isRegistered: false,
                error: 'Unable to verify guest email',
            },
            { status: 500 },
        );
    }
};
